/**
 * カテゴリマッピング定義
 * microCMSのセレクトフィールド値と表示用情報のマッピング
 */

import type { Column } from '@/types/microcms/column'

export interface CategoryInfo {
  displayName: string
  microcmsValue: string
  slug: string
  description?: string
}

export type CategoryCount = CategoryInfo & { count: number }

const CATEGORY_MAPPING: Record<string, CategoryInfo> = {
  product: {
    displayName: 'プロダクト情報',
    microcmsValue: 'プロダクト情報',
    slug: 'product',
    description: 'LAWGUEの製品情報と、開発の考え方をまとめたコラムの一覧です。'
  },
  report: {
    displayName: 'セミナー・イベント',
    microcmsValue: 'セミナー・イベント',
    slug: 'report',
    description:
      'LAWGUEのセミナー、ユーザー交流会、ユーザー対談のレポートをまとめたコラムの一覧です。'
  },
  usage: {
    displayName: '使い方コンテンツ',
    microcmsValue: '使い方コンテンツ',
    slug: 'usage',
    description:
      '契約書のバージョン管理や、規程同士の比較など、LAWGUEの使い方と活用のコツをまとめたコラムの一覧です。'
  },
  interview: {
    displayName: 'インタビュー',
    microcmsValue: 'インタビュー',
    slug: 'interview',
    description:
      'LAWGUEのユーザーや専門家へのインタビューをまとめたコラムの一覧です。'
  },
  contract: {
    displayName: '契約書ネタ',
    microcmsValue: '契約書ネタ',
    slug: 'contract',
    description:
      '契約書の書き方、リーガルチェックのポイント、英文契約書の扱いなど、契約書の実務に役立つコラムの一覧です。'
  },
  regulations: {
    displayName: '規程ネタ',
    microcmsValue: '規程ネタ',
    slug: 'regulations',
    description:
      '社内規程の作り方や、規程管理システムの選び方など、社内規程の実務に役立つコラムの一覧です。'
  },
  'legal-affairs': {
    displayName: '法務ネタ',
    microcmsValue: '法務ネタ',
    slug: 'legal-affairs',
    description:
      '法務管理の課題とリスク対策、リーガルテックや生成AIの活用など、法務の実務に役立つコラムの一覧です。'
  }
}

/**
 * microCMSの値からカテゴリ情報を取得
 */
export function getCategoryByMicrocmsValue(
  microcmsValue: string
): CategoryInfo | undefined {
  return Object.values(CATEGORY_MAPPING).find(
    (category) => category.microcmsValue === microcmsValue
  )
}

/**
 * slugからカテゴリ情報を取得
 */
export function getCategoryBySlug(slug: string): CategoryInfo | undefined {
  return CATEGORY_MAPPING[slug]
}

/**
 * 全カテゴリ情報を取得
 * microCMSのフィールド定義と同じ順番（逆順）で返す
 */
export function getAllCategories(): CategoryInfo[] {
  const order = [
    'regulations',
    'legal-affairs',
    'contract',
    'interview',
    'usage',
    'report',
    'product'
  ]
  return order.map((key) => CATEGORY_MAPPING[key]).filter(Boolean)
}

/**
 * コラムの columnCategory は文字列の配列 / オブジェクトのどちらでも来るので、
 * カテゴリ名（microCMS の値）の配列に揃える
 */
export function getColumnCategoryNames(
  columnCategory: Column['columnCategory']
): string[] {
  if (!columnCategory) return []
  return Array.isArray(columnCategory) ? columnCategory : [columnCategory.name]
}

/**
 * getAllCategories の順でカテゴリとコラム数を返す。コラムが 1 件も無いカテゴリは除く
 */
export function getCategoryCounts(
  columns: Pick<Column, 'columnCategory'>[]
): CategoryCount[] {
  return getAllCategories()
    .map((category) => ({
      ...category,
      count: columns.filter((column) =>
        getColumnCategoryNames(column.columnCategory).includes(
          category.microcmsValue
        )
      ).length
    }))
    .filter((category) => category.count > 0)
}
